/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import IconButton from '~components/_common/IconButton/IconButton';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

interface ImageViewerProps {
  fileUrl: string;
  onInternalClose?: () => void;
}

const imageMargin = 20;

const getResizedScale = (image: EventTarget & HTMLImageElement): number => {
  const container = document.querySelector('#container');

  const containerWidth = container?.clientWidth;
  const containerHeight = container?.clientHeight;
  const imageWidth = image.naturalWidth;
  const imageHeight = image.naturalHeight;

  let scale = 1;

  if (!containerHeight || !containerWidth) {
    return scale;
  }

  if (imageWidth < containerWidth && imageHeight < containerHeight) {
    return scale;
  }

  const heightDifference = imageHeight - containerHeight;
  const widthDifference = imageWidth - containerWidth;

  if (heightDifference > widthDifference) {
    scale = scale - (heightDifference + imageMargin) / imageHeight;
  }

  if (widthDifference > heightDifference) {
    scale = scale - (widthDifference + imageMargin) / imageWidth;
  }

  return scale;
};

const ImageViewer: React.FC<ImageViewerProps> = ({
  fileUrl,
  onInternalClose,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [imageRenderError, setImageErrorRender] = useState(false);
  const { setFileToView } = useFileViewerContext();
  const [initialScale, setInitialScale] = useState(1);

  useEffect(() => {
    setImageErrorRender(false);
  }, [fileUrl]);

  return (
    <TransformWrapper
      initialScale={initialScale}
      limitToBounds={true}
      minScale={0.1}>
      {({ zoomIn, zoomOut, resetTransform, centerView }) => (
        <div id="container" css={styles.container}>
          <div css={styles.toolBar}>
            <IconButton
              click={() =>
                onInternalClose ? onInternalClose() : setFileToView(undefined)
              }
              icon={['far', 'times']}
              customStyle={styles.icons}
              tooltip={intl.formatMessage({ id: 'close' })}
              backgroundOff
            />

            <IconButton
              icon={['far', 'search-plus']}
              customStyle={styles.icons}
              tooltip={intl.formatMessage({ id: 'zoom-in' })}
              click={() => zoomIn()}
            />
            <IconButton
              icon={['far', 'search-minus']}
              click={() => zoomOut()}
              tooltip={intl.formatMessage({ id: 'zoom-out' })}
              customStyle={styles.icons}
            />
            <IconButton
              icon={['far', 'redo']}
              click={() => resetTransform()}
              tooltip={intl.formatMessage({ id: 'center' })}
              customStyle={styles.icons}
            />
          </div>
          {imageRenderError && (
            <div css={styles.errorMessageContainer}>
              <p css={styles.errorMessage}>
                {intl.formatMessage({ id: 'opened-image-viewer-error' })}
              </p>
            </div>
          )}
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
            }}>
            {!imageRenderError && (
              <img
                css={styles.image}
                onLoad={(e) => {
                  const scale = getResizedScale(e.currentTarget);

                  if (scale === initialScale) {
                    return;
                  }

                  setInitialScale(scale);
                  centerView(scale, 0);
                }}
                onError={() => setImageErrorRender(true)}
                src={fileUrl}
                alt="viewer"
              />
            )}
          </TransformComponent>
        </div>
      )}
    </TransformWrapper>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    borderLeft: '1px solid #cccccc',
  },
  toolBar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#eeeeee',
    columnGap: '10px',
    height: '35px',
    padding: '4px 0px',
    borderRight: '1px solid #cccccc',
    borderTop: '1px solid #cccccc',
    borderBottom: '1px solid #cccccc',
  },
  icons: {
    fontSize: '18px',
    transition: '0.2s',
    margin: '10px 14px',
    color: '#3C435C',
  },

  errorMessageContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorMessage: {
    color: theme.colors.red,
    fontSize: 24,
    width: '50%',
    textAlign: 'center',
    border: `1px solid ${theme.colors.red}`,
    backgroundColor: theme.colors.white,
    borderRadius: 9,
    padding: '20px 20px',
    boxShadow: '0px 0px 19px rgba(0, 0, 0, 0.07)',
  },
  image: {
    boxShadow:
      '2px 10px 15px 5px rgba(0,0,0,0.1), 2px 4px 6px 5px rgba(0,0,0,0.05)',
  },
});

export default React.memo(ImageViewer);
